import type { FunctionComponent } from "react";
import { graphql, usePaginationFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { useInfiniteScrollTriggerReference } from "scmp-app/lib/hooks";
import type { topicListPaginationQuery } from "scmp-app/queries/__generated__/topicListPaginationQuery.graphql";
import type { topicListQuery$key } from "scmp-app/queries/__generated__/topicListQuery.graphql";

import { Container, StyledEntityLink, StyledLoading, TopicsContainer } from "./styles";

export type Props = {
  reference: topicListQuery$key;
};

export const TopicList: FunctionComponent<Props> = ({ reference, ...attributes }) => {
  const {
    data,
    hasNext: hasNextPage,
    isLoadingNext: isLoading,
    loadNext,
  } = usePaginationFragment<topicListPaginationQuery, topicListQuery$key>(
    graphql`
      fragment topicListQuery on Query
      @refetchable(queryName: "topicListPaginationQuery")
      @argumentDefinitions(scmpPlusTypes: { type: "[String]", defaultValue: [] }) {
        topics(
          exclude: { types: $scmpPlusTypes }
          filter: {
            name: { operator: $operator, value: $name }
            published: $published
            enabled: $enabled
          }
          orderBy: { field: NAME, direction: ASC }
          first: $first
          after: $after
          applicationId: $applicationId
        ) @connection(key: "topicListQuery__topics") {
          edges {
            node {
              __typename
              ...entityLink
              entityId
              name
            }
          }
        }
      }
    `,
    reference,
  );

  const { infiniteScrollTriggerReference } = useInfiniteScrollTriggerReference({
    hasNextPage,
    isLoading,
    onLoadMore: () => loadNext(100),
  });

  if (data.topics?.edges?.length === 0) return null;

  return (
    <Container {...attributes}>
      <TopicsContainer>
        {data.topics?.edges.filter(notEmpty).map(({ node }) => (
          <StyledEntityLink key={node.entityId} reference={node}>
            {node.name}
          </StyledEntityLink>
        ))}
      </TopicsContainer>
      {hasNextPage && <StyledLoading ref={infiniteScrollTriggerReference} />}
    </Container>
  );
};

TopicList.displayName = "TopicList";
